import type { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import {
  consumeGoogleRedirect,
  getGoogleRedirectFromState,
  handleGoogleCallback,
  setGoogleAuth,
} from '../auth/google';
import { googleOAuthExchange } from '../api/google/oauthExchange';
import { useToast } from '../components/shared/Toast';
import { vscodeTheme } from '../styles/theme';

export function GoogleCallback() {
  const [message, setMessage] = useState('認証処理中です…');
  const { showToast } = useToast();

  useEffect(() => {
    let timeoutId: number | undefined;

    const handleCallback = async () => {
      const { token, code, error } = handleGoogleCallback();

      if (error) {
        setMessage(`Google認証に失敗しました: ${error}`);
        showToast(`Google認証に失敗しました: ${error}`, 'error');
        timeoutId = window.setTimeout(() => window.history.back(), 3000);
        return;
      }

      if (token) {
        const redirect = getGoogleRedirectFromState() ?? consumeGoogleRedirect();
        setMessage('認証が完了しました。元の画面へ戻ります…');
        window.location.replace(redirect ?? '/');
        return;
      }

      if (code) {
        setMessage('認証コードを受信しました。トークン交換中です…');
        try {
          const response = await googleOAuthExchange({
            code,
            redirect_uri: `${window.location.origin}/callback/google`,
          });
          const expiresAt =
            response.expires_in != null ? Date.now() + response.expires_in * 1000 : undefined;
          setGoogleAuth({ accessToken: response.access_token, expiresAt });
          const redirect = getGoogleRedirectFromState() ?? consumeGoogleRedirect();
          setMessage('認証が完了しました。元の画面へ戻ります…');
          window.location.replace(redirect ?? '/');
          return;
        } catch (err) {
          const axiosError = err as AxiosError<{ error?: string; error_description?: string }>;
          const errorMessage =
            axiosError?.response?.data?.error_description ??
            axiosError?.response?.data?.error ??
            (err instanceof Error ? err.message : 'トークン交換に失敗しました');
          setMessage(`トークン交換に失敗しました: ${errorMessage}`);
          showToast(`トークン交換に失敗しました: ${errorMessage}`, 'error');
          timeoutId = window.setTimeout(() => window.history.back(), 3000);
          return;
        }
      }

      setMessage('認証情報が見つかりませんでした。再ログインしてください。');
    };

    void handleCallback();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [showToast]);

  return (
    <div
      style={{
        backgroundColor: vscodeTheme.colors.editorBackground,
        color: vscodeTheme.colors.textPrimary,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: vscodeTheme.typography.fontFamily,
        padding: '24px',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: '520px' }}>
        <h1 style={{ fontSize: '20px', marginBottom: '12px' }}>Google認証</h1>
        <p style={{ color: vscodeTheme.colors.textSecondary }}>{message}</p>
      </div>
    </div>
  );
}
